import { useScreenHelp } from "./ScreenHelp";
import React, { useEffect, useMemo, useState } from "react";
import CqcSiteOnboarding from "./CqcSiteOnboarding";
import CqcWorkspace from "./CqcWorkspace";
import CqcAddressForm from "./CqcAddressForm";
import { addressReady, countryOf, frameworkLabel } from "./cqcFrameworks";
import CqcServiceProfile from "./CqcServiceProfile";
import CqcProgress, { ProgressBar } from "./CqcProgress";
import { cqcApi } from "./cqcApi";
import {
  buildCqcRows,
  readiness,
  profileReady,
  catalogue,
} from "./cqcProfileModel";
import { fmtPct, RatingBadge } from "./cqcColors";
import "./cqc-workspace.css";

export default function CqcSites() {
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [view, setView] = useState({ mode: "group" });
  async function load() {
    try {
      const [s, a, p, w] = await Promise.all([
        cqcApi.listSites(),
        cqcApi.listAttestations(),
        cqcApi.listApplicability(),
        cqcApi.listAssignments(),
      ]);
      setData({
        sites: s.sites || [],
        attestations: a.attestations || [],
        applicability: p.applicability || [],
        assignments: w.assignments || [],
      });
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);
  const rows = useMemo(() => buildCqcRows(data || {}), [data]);
  const sites = data?.sites || [],
    active = sites.find((s) => s._id === view.siteId);
  useScreenHelp({
    title: "Care compliance — group overview",
    blurb: data ? `${sites.length} selected organisations, each with its own service profile, national evidence plan and progress indicators. Combined readiness is not a regulator rating.` : "Loading the organisations and care evidence available to your group.",
    can: data ? [sites.some(s => !addressReady(s)) ? "Confirm the missing organisation addresses first so the appropriate country framework can be selected." : "Review the readiness indicators and expand a country to inspect its organisations.", sites.some(s => !profileReady(s)) ? "Complete or review the service profiles flagged as incomplete." : "Open an organisation to review its overview, evidence setup or worksheet.", "Use Open combined overview / worksheet to review organisations together." ] : [error ? "Use Retry to try loading the group again." : "Wait for the group information to load."],
    related: ["care-group", "care-profile", "care-worksheet"]
  }, 10);
  async function saved(site) {
    await load();
    setView({ mode: "organisation", siteId: site._id });
  }
  if (!data)
    return (
      <div className="cqc-workspace">
        {error ? (
          <p role="alert">
            {error} <button onClick={load}>Retry</button>
          </p>
        ) : (
          "Loading CQC organisations…"
        )}
      </div>
    );
  return (
    <div className="cqc-workspace">
      {error && (
        <p role="alert" className="cqc-error">
          {error}
        </p>
      )}
      {view.mode !== "group" && (
        <button
          className="cqc-button"
          onClick={() => {
            load();
            setView({ mode: "group" });
          }}
        >
          ← Group overview
        </button>
      )}
      {view.mode === "import" || !sites.length ? (
        <CqcSiteOnboarding
          onImported={async () => {
            await load();
            setView({ mode: "group" });
          }}
        />
      ) : active &&
        (view.mode === "address" ||
          ((view.mode === "profile" || view.mode === "organisation") &&
            !addressReady(active))) ? (
        <CqcAddressForm
          key={`${active._id}-${active.addressRevision || 0}`}
          site={active}
          onSaved={async (site) => {
            await load();
            setView({ mode: "profile", siteId: site._id });
          }}
          onCancel={() => setView({ mode: "group" })}
        />
      ) : view.mode === "profile" && active ? (
        <CqcServiceProfile
          key={`${active._id}-${active.profileRevision || 0}`}
          site={active}
          onSaved={saved}
          onCancel={() => setView({ mode: "organisation", siteId: active._id })}
        />
      ) : view.mode === "organisation" && active ? (
        <CqcWorkspace
          key={active._id}
          initialSiteId={active._id}
          lockSite
          onConfigureProfile={() =>
            setView({ mode: "profile", siteId: active._id })
          }
        />
      ) : view.mode === "all" ? (
        <CqcWorkspace />
      ) : (
        <>
          <div
            className="cqc-toolbar"
            style={{ justifyContent: "space-between" }}
          >
            <div>
              <h2 style={{ margin: 0 }}>Care compliance — group overview</h2>
              <p className="cqc-muted">
                All selected organisations · each with its own service profile,
                worksheet and evidence plan
              </p>
            </div>
            <button
              className="cqc-button active"
              onClick={() => setView({ mode: "import" })}
            >
              Select / add organisations
            </button>
          </div>
          <CqcProgress
            rows={rows}
            sites={sites}
            onSelect={(siteId) => setView({ mode: "organisation", siteId })}
          />
          <div className="cqc-toolbar">
            <button
              className="cqc-button"
              onClick={() => setView({ mode: "all" })}
            >
              Open combined overview / worksheet
            </button>
          </div>
          <div className="cqc-organisations">
            {sites.map((site) => {
              const stats = readiness(
                  rows.filter((r) => r.site._id === site._id),
                ),
                ready = profileReady(site);
              const labels = (site.serviceProfile?.serviceTypes || [])
                .map(
                  (id) =>
                    catalogue.services.find((s) => s.id === id)?.label || id,
                )
                .join(" + ");
              return (
                <article className="cqc-card" key={site._id}>
                  <h3>{site.name}</h3>
                  <p className="cqc-muted">
                    {countryOf(site) || "Country not confirmed"} ·{" "}
                    {site.source === "manual"
                      ? site.registrationReference ||
                        "Registration reference not recorded"
                      : `CQC location ${site.cqcLocationId}`}{" "}
                    · {site.address?.postcode || site.postcode}
                  </p>
                  <p>{labels || "Services not configured"}</p>
                  <p className="cqc-muted">{frameworkLabel(site)}</p>
                  {countryOf(site) === "England" && site.ratings?.overall && (
                    <p>
                      <RatingBadge rating={site.ratings.overall} />{" "}
                      <span className="cqc-muted">Published CQC rating</span>
                    </p>
                  )}
                  <ProgressBar stats={stats} />
                  <p>
                    {ready
                      ? `${fmtPct(stats.pct)} ready · ${stats.completed}/${stats.total} reviewed`
                      : !addressReady(site)
                        ? "Address and country required"
                        : "Service profile required"}
                  </p>
                  <div className="cqc-toolbar">
                    <button
                      className="cqc-button active"
                      onClick={() =>
                        setView({
                          mode: !addressReady(site)
                            ? "address"
                            : ready
                              ? "organisation"
                              : "profile",
                          siteId: site._id,
                        })
                      }
                    >
                      {!addressReady(site)
                        ? "Add / confirm address"
                        : ready
                          ? "Overview / worksheet"
                          : "Create service profile"}
                    </button>
                    <button
                      className="cqc-button"
                      onClick={() =>
                        setView({ mode: "profile", siteId: site._id })
                      }
                    >
                      Edit profile
                    </button>
                    <button
                      className="cqc-button"
                      onClick={() =>
                        setView({ mode: "address", siteId: site._id })
                      }
                    >
                      Edit address
                    </button>
                    {site.source !== "manual" && (
                      <button
                        className="cqc-button"
                        onClick={async () => {
                          try {
                            await cqcApi.resyncSite(site._id);
                            await load();
                          } catch (e) {
                            setError(e.message);
                          }
                        }}
                      >
                        Refresh CQC registration
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
